import axios, {
  AxiosInstance,
  AxiosError,
  AxiosRequestConfig,
  ResponseType as AxiosResponseType,
} from 'axios';

import { HttpClient, RequestOptions, ResponseType } from './HttpClientInterface.js';
import { Response } from './Response.js';
import { toBearer } from './headers.js';
import { HttpError, isStatusErrorPayload } from './HttpError.js';
import * as qs from 'qs';

function toAxiosResponseType(responseType: ResponseType): AxiosResponseType {
  let axiosResponseType: AxiosResponseType = 'json';

  if (responseType === ResponseType.STREAM) {
    axiosResponseType = 'stream';
  }

  return axiosResponseType;
}

function handleError(err: AxiosError, originalStack: string = ''): never {
  const payload = err.response?.data;

  const combinedMessage = `
  Original Message: ${err.message}

  Additional Details: 
    Endpoint: [${err.config.method}] ${err.config.url}`
  const combinedStack = `
  HttpClient Stack: ${err.stack} 

  Originating Stack: ${originalStack}`
  if (isStatusErrorPayload(payload)) {
    throw new HttpError(err.name, `${combinedMessage}\n\n  Payload Message: ${payload.message}`, payload.statusCode,
      `${combinedStack}\n\n  Payload Stack: ${payload.stack}`, payload);
  }

  throw new HttpError(err.name, combinedMessage, err.response?.status, combinedStack, err.response?.data);
}

export class AxiosHttpClient implements HttpClient {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create();
  }

  get<ResponsePayload>(url: string): Promise<Response<ResponsePayload>> {
    return this.request({ method: 'get', url });
  }

  post<RequestPayload, ResponsePayload>(
    url: string,
    body?: RequestPayload
  ): Promise<Response<ResponsePayload>> {
    return this.request({ method: 'post', url, body });
  }

  delete<RequestPayload, ResponsePayload>(
    url: string,
    body?: RequestPayload
  ): Promise<Response<ResponsePayload>> {
    return this.request({ method: 'delete', url, body });
  }

  put<RequestPayload, ResponsePayload>(
    url: string,
    body?: RequestPayload
  ): Promise<Response<ResponsePayload>> {
    return this.request({ method: 'put', url, body });
  }

  patch<RequestPayload, ResponsePayload>(
    url: string,
    body?: RequestPayload
  ): Promise<Response<ResponsePayload>> {
    return this.request({ method: 'patch', url, body });
  }

  request<RequestPayload, ResponsePayload, QueryParams>({
    method,
    url,
    headers,
    body,
    queryParams,
    responseType,
    withCredentials,
  }: RequestOptions<RequestPayload, QueryParams>): Promise<Response<ResponsePayload>> {
    let fullUrl = url;

    if (queryParams) {
      fullUrl += `?${qs.stringify(queryParams)}`;
    }

    const requestConfig: AxiosRequestConfig = {
      method,
      url: fullUrl,
      headers,
      data: body,
    };
    if (withCredentials) {
      requestConfig.withCredentials = withCredentials;
    }
    if (responseType) {
      requestConfig.responseType = toAxiosResponseType(responseType);
    }

    const originalStack = new Error().stack;

    return this.instance(requestConfig).catch((err) => handleError(err, originalStack));
  }

  setAuthToken(authToken: string): void {
    this.instance.defaults.headers.Authorization = toBearer(authToken);
  }

  clearAuthToken(): void {
    delete this.instance.defaults.headers.Authorization;
  }
}

